import React, { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';

import { Prompt, PublicPrompt } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExText } from 'jf/components/DevExText';
import { DevExTextArea } from 'jf/components/DevExTextArea';

import { LikertScale, LikertScaleLabels } from './LikertScale';
import { NetPromoterScore } from './NetPromoterScore';
import { QuestionCard, QuestionCardProps } from './QuestionCard';

export type PromptValueType = number | string | Record<string, number>;

interface QuestionCardPromptProps extends Omit<QuestionCardProps, 'onBack' | 'onNext'> {
  number: number;
  onBack?: (value?: PromptValueType) => void;
  onNext?: (value?: PromptValueType) => void;
  onSkip?: () => void;
  prompt: PublicPrompt | Prompt;
  promptRequired?: boolean;
  tag?: string;
  initialValue?: PromptValueType;
  active?: boolean;
  loading?: boolean;
}

interface MatrixPromptProps {
  value?: Record<string, number>;
  rowLabels: string[];
  polarity: number;
  choiceLabels?: string[];
  disabled: boolean;
  numeric?: boolean; // if true, uses numeric values for the scale
  onChange: (value: Record<string, number>) => void;
}

const MatrixPrompt = ({
  value,
  rowLabels,
  polarity,
  choiceLabels,
  disabled,
  numeric,
  onChange,
}: MatrixPromptProps) => {
  const [promptValue, setPromptValue] = useState<Record<string, number>>(value ?? {});
  const theme = useDevExTheme();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(10%, 20%) 1fr',
        gap: theme.variable.spacing.sm,
        marginBottom: theme.variable.spacing.md,
      }}
    >
      {/* Empty cell for first column, first row */}
      <div></div>

      {/* LikertScaleLabels in second column, first row */}
      <LikertScaleLabels
        choiceLabels={choiceLabels}
        numeric={numeric}
        style={{ marginBottom: theme.variable.spacing.xs }}
      />

      {/* Row labels and scales */}
      {rowLabels.map((label, index) => (
        <React.Fragment key={index}>
          <DevExText style={{ alignSelf: 'center' }}>{label}</DevExText>

          <LikertScale
            polarity={polarity}
            disabled={disabled}
            choiceLabels={choiceLabels}
            value={promptValue[label]}
            onChange={(value) => {
              const newMatrixValue = { ...promptValue, [label]: value };
              setPromptValue(newMatrixValue);
              onChange(newMatrixValue);
            }}
            numeric={numeric}
            type="matrix"
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export const QuestionCardPrompt: React.FC<QuestionCardPromptProps> = (props) => {
  const theme = useDevExTheme();
  const { prompt, promptRequired, ...cardProps } = props;
  const [value, setValue] = useState(props.initialValue);

  const isLikert = prompt.type === Prompt.type.RATING_5L || prompt.type === Prompt.type.RATING_5N;
  const isOpenEnded = prompt.type === Prompt.type.OPEN_END;
  const isMatrix = prompt.type === Prompt.type.MATRIX_N || prompt.type === Prompt.type.MATRIX_L;
  const isNPS = prompt.type === Prompt.type.NPS;

  const validated = useMemo(() => {
    if (typeof value === 'string') {
      return !!value.trim();
    } else if (typeof value === 'number') {
      // Return true to handle valid falsy values like 0
      return true;
    }

    if (isMatrix) {
      return !!(
        value &&
        typeof value === 'object' &&
        prompt.rowLabels!.every((label) => typeof value[label] === 'number')
      );
    }
    return !!value;
  }, [value]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ([1, 2, 3, 4, 5].includes(Number(event.key))) {
        if (props.active) {
          if (isLikert) {
            setValue(Number(event.key));
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [props.active]);

  return (
    <QuestionCard
      title={props.prompt.text}
      subtitle={<span>Question {props.number}</span>}
      tag={props.tag}
      validated={validated}
      auto={isLikert}
      {...cardProps}
      onBack={props.onBack && (() => props.onBack?.(value))}
      onNext={props.onNext && (() => props.onNext?.(value))}
      onSkip={!promptRequired ? props.onSkip && (() => props.onSkip?.()) : undefined}
    >
      {isMatrix && (
        <MatrixPrompt
          value={value as Record<string, number>}
          rowLabels={prompt.rowLabels!}
          polarity={prompt.polarity}
          numeric={prompt.type === Prompt.type.MATRIX_N}
          choiceLabels={prompt.choiceLabels}
          disabled={props.disabled ?? false}
          onChange={setValue}
        />
      )}
      {isLikert && (
        <LikertScale
          value={value as number}
          onChange={setValue}
          polarity={prompt.polarity}
          numeric={prompt.type === Prompt.type.RATING_5N}
          disabled={props.disabled}
          choiceLabels={prompt.choiceLabels}
        />
      )}
      {isOpenEnded && (
        <Flex style={{ paddingBottom: theme.variable.spacing.md }}>
          <DevExTextArea
            value={value as string}
            onChange={setValue}
            maxLength={1000}
            showCount={{ formatter: ({ count }) => (count >= 900 ? `${count} / 1000` : null) }}
            autoSize={{ minRows: 3 }}
          />
        </Flex>
      )}
      {isNPS && (
        <NetPromoterScore
          value={value as number}
          onChange={setValue}
          numeric={prompt.type === Prompt.type.NPS}
          disabled={props.disabled}
          choiceLabels={prompt.choiceLabels}
        />
      )}
    </QuestionCard>
  );
};
